/**
 * Wall-clock helpers pinned to the company's operating timezone.
 *
 * Activity forms (meetings, phone calls) log events "as they happen", so the
 * date they prefill has to match the date the server validates against. The
 * browser clock can sit on either side of midnight relative to Lithuania, so
 * both sides read Vilnius time instead of whatever the device is set to.
 *
 * Mirrors `config('app.business_timezone')` on the backend.
 */
export const BUSINESS_TIME_ZONE = 'Europe/Vilnius';

const partsFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: BUSINESS_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
});

function businessParts(instant: Date): Record<string, string> {
    return Object.fromEntries(partsFormatter.formatToParts(instant).flatMap((part) => (part.type === 'literal' ? [] : [[part.type, part.value]])));
}

/**
 * The current instant re-expressed as a local Date carrying Vilnius wall-clock
 * values, so `date-fns` formatting and the calendar (both of which read local
 * getters) render Lithuanian time.
 */
export function businessNow(): Date {
    const parts = businessParts(new Date());

    return new Date(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second));
}

/** Today's date in Vilnius as `yyyy-MM-dd`. */
export function businessToday(): string {
    const parts = businessParts(new Date());

    return `${parts.year}-${parts.month}-${parts.day}`;
}

/** Current Vilnius wall-clock time as `HH:mm`. */
export function businessTimeNow(): string {
    const parts = businessParts(new Date());

    return `${parts.hour}:${parts.minute}`;
}

/**
 * True when a calendar day sits after today in Vilnius. Compares plain
 * `yyyy-MM-dd` strings so the browser's own offset never leaks in.
 */
export function isAfterBusinessToday(day: Date): boolean {
    const year = day.getFullYear().toString().padStart(4, '0');
    const month = (day.getMonth() + 1).toString().padStart(2, '0');
    const date = day.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${date}` > businessToday();
}
